import React from 'react';

import dayjs from 'dayjs';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import {
    BILLING_TYPE_MAP,
    CALCULATION_TYPE_MAP,
    CAPPING_MAP,
    CURRENCY_MAP,
    FREE_TIME_TYPE_MAP,
    linkedEntitiesTypeTranslationMap,
    TICK_TYPE_MAP,
} from 'src/features/pricingPlan/utils/constants';
import { PricingPlanFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import {
    BILLING_TYPE,
    CALCULATION_TYPE,
    CAPPING,
    FREE_TIME_TYPE,
    TICK_TYPE,
    TransformedLinkedEntities,
} from 'src/features/pricingPlan/utils/types';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import { Badge } from 'src/registry/ui/Badge';
import { Checkbox } from 'src/registry/ui/Checkbox';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Separator } from 'src/registry/ui/Separator';
import { CURRENCY } from 'src/types';
import { generalMessages } from 'src/utils/messages';

type PricingPlanFormProps = {
    form: UseFormReturn<PricingPlanFormSchemaValues>;
    isNewVersion?: boolean;
    setUseInRecordsDate: React.Dispatch<React.SetStateAction<string | undefined>>;
    linkedEntities?: TransformedLinkedEntities;
    isPublished?: boolean;
};

const translationFields = [
    { key: 'name', label: generalMessages.name },
    { key: 'description', label: generalMessages.description },
] as const;

function PricingPlanForm({
    form,
    isNewVersion = false,
    setUseInRecordsDate,
    linkedEntities,
    isPublished = false,
}: PricingPlanFormProps) {
    const { formatMessage } = useIntl();

    const settingsData = React.useContext(SettingsContext);

    const { total_active_stays, total_active_memberships } = linkedEntities ?? {};

    const totalAffectedEntitiesCount = linkedEntities?.total_entities_count ?? 0;

    const totalActiveStaysMessage = total_active_stays
        ? formatMessage(pricingPlanMessages.numberOfActiveStays, { count: total_active_stays })
        : '';
    const totalActiveMembershipsMessage = total_active_memberships
        ? formatMessage(pricingPlanMessages.numberOfActiveMemberships, { count: total_active_memberships })
        : '';
    const affectedRecordsMessage =
        total_active_stays && total_active_memberships
            ? `${totalActiveStaysMessage} ${formatMessage(pricingPlanMessages.and)} ${totalActiveMembershipsMessage}`
            : `${totalActiveStaysMessage}${totalActiveMembershipsMessage}`;

    const { fields } = useFieldArray({
        control: form.control,
        name: 'translations',
    });

    const { translations, calculation_type, tick, extra_free_time, valid_from } = form.watch();

    React.useEffect(() => {
        setUseInRecordsDate(valid_from);
    }, [setUseInRecordsDate, valid_from]);

    const calculationTypeValue = React.useMemo(() => {
        if (!tick?.tick_type) return 1;
        switch (tick.tick_type) {
            case TICK_TYPE.MINUTE:
                return 1;
            case TICK_TYPE.HOUR:
                return 60;
            case TICK_TYPE.DAY:
                return 60 * 24;
            case TICK_TYPE.WEEK:
                return 60 * 24 * 7;
            case TICK_TYPE.MONTH:
                return 60 * 24 * 30;
        }
    }, [tick?.tick_type]);

    React.useEffect(() => {
        if (calculation_type !== CALCULATION_TYPE.PER_FIXED_TICK) form.setValue('tick', null);
        if (calculation_type !== CALCULATION_TYPE.FIXED_PRICE) form.setValue('fixed_rate', null);
        if (calculation_type === CALCULATION_TYPE.FIXED_PRICE) {
            // The "rules" section is hidden for fixed-price plans, so clear its (unused) values.
            form.setValue('free_time_type', null);
            form.setValue('free_time_minutes', null);
            form.setValue('free_time_cap_minutes', null);
            form.setValue('exit_time', null);
            form.setValue('exit_time_grace', null);
        }
    }, [calculation_type, form]);

    React.useEffect(() => {
        if (!extra_free_time) {
            form.setValue('extra_free_time_minutes', null);
            form.setValue('extra_free_time_activation_remaining_minutes', null);
        }
    }, [form, extra_free_time]);

    // We check if the converted value is not integer, in that case we default it to 0
    React.useEffect(() => {
        if (!tick?.tick_minutes || Number.isInteger(tick.tick_minutes / calculationTypeValue)) return;
        form.setValue('tick.tick_minutes', 0);
    }, [tick?.tick_type, calculationTypeValue, form.setValue, tick?.tick_minutes, form]);

    React.useEffect(() => {
        if (!settingsData?.languages) return;
        form.setValue(
            'translations',
            settingsData.languages.map((lang, index) => ({
                language: lang,
                name: translations[index]?.name || '',
                description: translations[index]?.description || '',
            })),
        );
    }, [settingsData, form]);

    const disableFormField = isPublished && !isNewVersion;

    const CalculationTypeComponent = React.useCallback(() => {
        switch (calculation_type) {
            case CALCULATION_TYPE.PER_FIXED_TICK:
                return (
                    <>
                        <SelectFormField<PricingPlanFormSchemaValues>
                            form={form}
                            name="tick.tick_type"
                            label={formatMessage(pricingPlanMessages.tickType)}
                            options={Object.values(TICK_TYPE)}
                            optionTranslationMap={TICK_TYPE_MAP}
                            disabled={disableFormField}
                        />

                        <FormField
                            control={form.control}
                            name="tick.tick_minutes"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(pricingPlanMessages.tickDuration)}
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                pattern="^d+$"
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    field.onChange(
                                                        value === '' ? null : Number(value) * calculationTypeValue,
                                                    );
                                                }}
                                                value={field.value ? field.value / calculationTypeValue : ''}
                                                placeholder={`${formatMessage(pricingPlanMessages.tickDuration)}...`}
                                                disabled={disableFormField}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </>
                );

            case CALCULATION_TYPE.FIXED_PRICE:
                return (
                    <InputFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="fixed_rate.rate_with_tax"
                        label={formatMessage(pricingPlanMessages.rateWithTax)}
                        pattern={/^\s*-?\d*(\.\d{0,2})?\s*$/}
                        disabled={disableFormField}
                    />
                );
            default:
                return (
                    <SelectFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="capping"
                        label={formatMessage(pricingPlanMessages.capping)}
                        options={Object.values(CAPPING)}
                        optionTranslationMap={CAPPING_MAP}
                        disabled={disableFormField}
                    />
                );
        }
    }, [calculationTypeValue, calculation_type, form, formatMessage]);

    const allLinkedEntitiesSentence = linkedEntities?.transformedData?.reduce((sentence, item, index) => {
        const count = linkedEntities?.transformedData?.filter((entity) => entity.type === item.type).length;

        if (linkedEntities?.transformedData?.findIndex((entity) => entity.type === item.type) !== index) {
            return sentence;
        }

        return sentence
            ? `${sentence}, ${formatMessage(linkedEntitiesTypeTranslationMap[item.type], { value: count })}`
            : `${formatMessage(linkedEntitiesTypeTranslationMap[item.type], { value: count })}`;
    }, '');

    return (
        <Form form={form} variant="dialog">
            {isNewVersion && totalAffectedEntitiesCount > 0 && (
                <Badge
                    theme="primary"
                    className="min-h-16 w-full rounded-sm p-2 text-sm wrap-break-word whitespace-normal"
                >
                    <div className="w-full">
                        <span className="font-extrabold whitespace-nowrap">
                            {formatMessage(pricingPlanMessages.affectedRecordsCount, {
                                count: totalAffectedEntitiesCount,
                            })}
                        </span>

                        {formatMessage(pricingPlanMessages.newRulesDescription, {
                            detailedAffectedRecords: allLinkedEntitiesSentence ?? '',
                            date: dayjs(valid_from).format('DD.MM.YYYY HH:mm'),
                            affectedRecordsMessage,
                            count: (total_active_memberships ?? 0) + (total_active_stays ?? 0),
                        })}
                    </div>
                </Badge>
            )}
            {translationFields.map(({ key, label }) =>
                fields.map((_, index) =>
                    key === 'name' ? (
                        <InputFormField<PricingPlanFormSchemaValues>
                            key={`translations.${index}.${key}`}
                            form={form}
                            name={`translations.${index}.${key}`}
                            inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                            label={formatMessage(label)}
                        />
                    ) : (
                        <TextareaFormField<PricingPlanFormSchemaValues>
                            key={`translations.${index}.${key}`}
                            form={form}
                            name={`translations.${index}.${key}`}
                            inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                            label={formatMessage(label)}
                        />
                    ),
                ),
            )}
            <SelectFormField<PricingPlanFormSchemaValues>
                form={form}
                name="billing_type"
                label={formatMessage(pricingPlanMessages.billingType)}
                options={Object.values(BILLING_TYPE)}
                optionTranslationMap={BILLING_TYPE_MAP}
                disabled={disableFormField}
            />
            <SelectFormField<PricingPlanFormSchemaValues>
                form={form}
                name="calculation_type"
                label={formatMessage(pricingPlanMessages.calculationType)}
                options={[
                    CALCULATION_TYPE.PER_FIXED_TICK,
                    // Temporarily hidden until the backend logic for them is finished, so users cannot
                    // create plans with these calculation types yet
                    // CALCULATION_TYPE.PER_PERIODS_COMPOUND,
                    // CALCULATION_TYPE.PER_PERIODS_TOTAL,
                    CALCULATION_TYPE.FIXED_PRICE,
                ]}
                optionTranslationMap={CALCULATION_TYPE_MAP}
                disabled={disableFormField}
            />
            <CalculationTypeComponent />
            <SectionBlockTitle className="text-base">
                {formatMessage(pricingPlanMessages.taxAttributes)}
            </SectionBlockTitle>
            <Separator />
            <SelectFormField<PricingPlanFormSchemaValues>
                form={form}
                name="currency"
                label={formatMessage(pricingPlanMessages.currency)}
                options={Object.values(CURRENCY)}
                optionTranslationMap={CURRENCY_MAP}
                disabled={disableFormField}
            />
            <InputFormField<PricingPlanFormSchemaValues>
                form={form}
                name="tax_percent"
                label={formatMessage(pricingPlanMessages.taxPercent)}
                pattern={/^\s*-?\d*(\.\d{0,2})?\s*$/}
                disabled={disableFormField}
            />
            {calculation_type !== CALCULATION_TYPE.FIXED_PRICE && (
                <>
                    <SectionBlockTitle className="text-base">
                        {formatMessage(pricingPlanMessages.rules)}
                    </SectionBlockTitle>
                    <Separator />
                    <SelectFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="free_time_type"
                        label={formatMessage(pricingPlanMessages.freeTimeType)}
                        options={Object.values(FREE_TIME_TYPE)}
                        optionTranslationMap={FREE_TIME_TYPE_MAP}
                        disabled={disableFormField}
                    />
                    <InputFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="free_time_minutes"
                        type="number"
                        label={formatMessage(pricingPlanMessages.freeTime)}
                        placeholder={formatMessage(pricingPlanMessages.minutes)}
                        min={0}
                        disabled={disableFormField}
                    />
                    <InputFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="free_time_cap_minutes"
                        type="number"
                        label={formatMessage(pricingPlanMessages.freeTimeCap)}
                        placeholder={formatMessage(pricingPlanMessages.minutes)}
                        min={0}
                        disabled={disableFormField}
                    />
                    <InputFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="exit_time"
                        type="number"
                        label={formatMessage(pricingPlanMessages.exitTime)}
                        placeholder={formatMessage(pricingPlanMessages.minutes)}
                        min={0}
                        disabled={disableFormField}
                    />
                    <InputFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="exit_time_grace"
                        type="number"
                        label={formatMessage(pricingPlanMessages.exitTimeGrace)}
                        placeholder={formatMessage(pricingPlanMessages.minutes)}
                        min={0}
                        disabled={disableFormField}
                    />
                    <FormField
                        control={form.control}
                        name="extra_free_time"
                        render={({ field }) => (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog">
                                    <FormattedMessage {...pricingPlanMessages.extraFreeTime} />
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <Checkbox
                                            checked={!!field.value}
                                            onCheckedChange={(checked) => {
                                                const value = checked === true;
                                                field.onChange(value);
                                            }}
                                            disabled={disableFormField}
                                        />
                                    </FormControl>
                                </FormControlStack>
                            </FormItem>
                        )}
                    />
                    <InputFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="extra_free_time_minutes"
                        type="number"
                        label={formatMessage(pricingPlanMessages.extraFreeTimeMinutes)}
                        disabled={!extra_free_time || disableFormField}
                        required={Boolean(extra_free_time)}
                        min={0}
                    />
                    <InputFormField<PricingPlanFormSchemaValues>
                        form={form}
                        name="extra_free_time_activation_remaining_minutes"
                        type="number"
                        label={formatMessage(pricingPlanMessages.extraFreeTimeActivationRemainingMinutes)}
                        disabled={!extra_free_time || disableFormField}
                        required={Boolean(extra_free_time)}
                        moreInfo={formatMessage(pricingPlanMessages.extraFreeTimeActivationRemainingMinutesMoreInfo)}
                        min={0}
                    />
                </>
            )}
            <SectionBlockTitle className="text-base">{formatMessage(pricingPlanMessages.validity)}</SectionBlockTitle>
            <Separator />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(pricingPlanMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabled={disableFormField}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(pricingPlanMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabled={disableFormField}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default PricingPlanForm;
